import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  DocumentListCard,
  Unifree,
  NavigationSlider,
  feedbackcard,
  ErrorPage503
} from '@yes.technology/react-toolkit'

import { GridContainer } from 'style/grid'
import * as interactionApi from '../utils/api'
import { interactionModelOperations } from '../redux'
import InteractionStep from '../components/InteractionStep'
import ScrollToTop from '../../shared/ScrollToTop'
import Error from '../../shared/Error'
import Success from '../components/Success'
import { getActivitiesFromInteractionModel } from '../../../utils/interactionModel'
import hasPendingDecisionModel from '../utils/hasPendingDecisionModel'
import {
  useValidationCallbacks,
  BlueprintsContext,
  useInteractionModel,
  useGeolocation,
  useInitialConfig,
  useStep,
  useSequenceRules,
  useNavigation
} from '../hooks'
import moment from 'moment'
import InteractionInformation from '../components/InteractionInformation'
import { persistor, useAppDispatch, useAppSelector } from 'state/store'
import Loading from 'modules/shared/Loading'
import { StickyArea } from './InteractionContainer.styles'
import ReviewSaveButton from '../components/Review/components/ReviewSaveButton/ReviewSaveButton'
import { ReviewSaveButtonContextProvider } from '../components/Review/components/ReviewSaveButton/ReviewSaveButtonContext'
import { SelectAndLinkFrameContextProvider } from '../components/SelectAndLinkFrame/SelectAndLinkFrameContext'
import { Activity } from 'types/shared'
import formatBlueprintsResponseActivities from 'utils/formatBlueprintsResponseActivities'
import formatBlueprintsRequestActivities from 'utils/formatBlueprintsRequestActivities'
import { useLocation } from 'react-router-dom'

const InteractionContainer = () => {
  const { t, language } = useTranslation()

  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)

  const [isFetchingBlueprints, setIsFetchingBlueprints] = useState(false)
  const [isFirstBlueprintsRequest, setIsFirstBlueprintsRequest] = useState(true)

  const [blueprintActivities, setBlueprintActivities] = useState<Activity[]>([])
  const shouldApplyInitialDecisionModel = useRef(true)

  const dispatch = useAppDispatch()

  const apiState = useAppSelector((state) => state.api)

  const blueprintsPromise = useRef<Promise<void> | null>(null)
  const shouldExecuteInitialBlueprint = useRef(true)
  const shouldAutofillActivities = useRef(true)

  const {
    isFetchingInteractionModel,
    interactionModel,
    interactionModelError
  } = useInteractionModel({ currentTime: moment().format() })

  const workflowUuid = interactionModel.workflowUuid
  const interactionModelData = interactionModel.data

  useValidationCallbacks(interactionModel.data)
  useGeolocation(interactionModel.geolocation)
  useInitialConfig(interactionModel)

  const clearReduxPersist = () => {
    persistor.pause()
    persistor.flush().then(() => persistor.purge())
  }

  const {
    totalSteps,
    currentStep,
    currentStepData,
    changeStep,
    isLoadedSteps,
    isInteractionFinished
  } = useStep(interactionModel, clearReduxPersist)
  const showTextForTotal = hasPendingDecisionModel(interactionModel?.data)

  const { executeSequenceDecisionModel, applyInitialDecisionModel } =
    useSequenceRules()

  const { showDoubleArrowhead } = useNavigation(
    interactionModel?.data?.interaction_model_item_groups
  )

  const updateActivities = useCallback(
    (activities: Activity[]) => {
      dispatch(interactionModelOperations.updateActivities(activities))
    },
    [dispatch]
  )

  const executeBlueprints = useCallback(() => {
    if (!interactionModelData) return

    const interactionModelUuid = interactionModelData?.uuid

    const activities = getActivitiesFromInteractionModel(interactionModelData)

    setIsFetchingBlueprints(true)

    const blueprintsRequestActivities = formatBlueprintsRequestActivities(
      activities,
      interactionModelData,
      language
    )

    blueprintsPromise.current = interactionApi
      .executeBlueprints({
        interactionModelUuid,
        workflowUuid,
        activities: blueprintsRequestActivities
      })
      .then((response) => {
        if (response.success) {
          const formattedActivities = formatBlueprintsResponseActivities(
            response.data?.activities,
            interactionModelData,
            language
          )
          updateActivities(formattedActivities)

          setBlueprintActivities(response.data?.activities)
        }
      })
      .catch((error) => {
        if (isFirstBlueprintsRequest && error.status === 503) {
          dispatch({ type: 'api/ERROR', data: { status: error.status } })
          return
        }
        if (error.status === 503) {
          feedbackcard(t('system.unavailable'), {
            type: 'error',
            message: t('system.unavailable-des')
          })
        }
      })
      .finally(() => {
        setIsFetchingBlueprints(false)
        setIsFirstBlueprintsRequest(false)
        blueprintsPromise.current = null
      })
  }, [
    interactionModelData,
    workflowUuid,
    updateActivities,
    isFirstBlueprintsRequest,
    dispatch,
    t,
    language
  ])

  useEffect(() => {
    if (
      shouldExecuteInitialBlueprint.current &&
      !isFetchingInteractionModel &&
      interactionModelData?.uuid
    ) {
      shouldExecuteInitialBlueprint.current = false

      executeBlueprints()
    }
  }, [
    isFetchingInteractionModel,
    interactionModelData?.uuid,
    executeBlueprints
  ])

  useEffect(() => {
    if (
      shouldApplyInitialDecisionModel.current &&
      blueprintActivities.length &&
      interactionModelData?.interaction_model_item_groups
    ) {
      shouldApplyInitialDecisionModel.current = false

      applyInitialDecisionModel(
        blueprintActivities,
        interactionModelData?.interaction_model_item_groups
      )
    }
  }, [
    applyInitialDecisionModel,
    interactionModelData?.interaction_model_item_groups,
    blueprintActivities
  ])

  const handleChangeActivity = useCallback(
    (activity: Activity) => {
      executeSequenceDecisionModel(
        activity,
        currentStepData?.activity_models || [],
        interactionModelData?.interaction_model_item_groups || []
      )

      if (blueprintsPromise.current) {
        blueprintsPromise.current.then(() => updateActivities([activity]))

        return
      }

      updateActivities([activity])
    },
    [
      updateActivities,
      currentStepData?.activity_models,
      executeSequenceDecisionModel,
      interactionModelData?.interaction_model_item_groups
    ]
  )

  useEffect(() => {
    if (
      !searchParams ||
      !shouldAutofillActivities.current ||
      isFetchingInteractionModel
    )
      return

    shouldAutofillActivities.current = false
    const activities = JSON.parse(searchParams.get('activities') || '{}')

    Object.keys(activities).forEach((key) => {
      const activity = activities[key]
      handleChangeActivity({
        uuid_activity_model: key,
        des: activity?.des || activity,
        complement: activity?.complement
      })
    })
  }, [
    searchParams,
    handleChangeActivity,
    isFetchingInteractionModel,
    shouldAutofillActivities
  ])

  const shouldDisplayError =
    !isFetchingInteractionModel && interactionModelError

  const isSplashScreen =
    !shouldDisplayError && (isFetchingInteractionModel || !isLoadedSteps)

  if (isSplashScreen) {
    return <Loading active />
  }

  if (shouldDisplayError)
    return (
      <GridContainer>
        <Error errorMessage={interactionModelError} />
      </GridContainer>
    )

  if (apiState.isError && apiState.status === 503) {
    return (
      <GridContainer>
        <ErrorPage503 onTryAgain={() => window.location.reload()} />
      </GridContainer>
    )
  }

  return (
    <BlueprintsContext.Provider
      value={{ executeBlueprints, isFetchingBlueprints }}
    >
      <ScrollToTop />

      <InteractionInformation
        cards={interactionModelData?.information_cards || []}
        {...{ currentStep, isInteractionFinished }}
      />

      {!isInteractionFinished && (
        <>
          <GridContainer>
            <DocumentListCard className='my-3'>
              <Unifree
                informational
                label={t('interactionmodel')}
                value={interactionModelData?.des || ''}
                required
              />
            </DocumentListCard>
          </GridContainer>

          <ReviewSaveButtonContextProvider>
            <StickyArea>
              <NavigationSlider
                currentPosition={currentStep}
                first={1}
                total={totalSteps}
                hierarchyLevel='level_2'
                doubleArrowhead={showDoubleArrowhead}
                textForCurrent={t('navigation-slider.text-for-current')}
                textForTotal={
                  showTextForTotal
                    ? t('navigation-slider.text-for-total')
                    : undefined
                }
                afterClick={changeStep}
                heightClass='xSmall'
              />
              <ReviewSaveButton {...{ totalSteps, currentStep, changeStep }} />
            </StickyArea>

            <GridContainer>
              <SelectAndLinkFrameContextProvider>
                <InteractionStep
                  totalSteps={totalSteps}
                  currentStep={currentStep}
                  interactionItemGroup={currentStepData}
                  handleChangeStep={changeStep}
                  handleChangeActivity={handleChangeActivity}
                />
              </SelectAndLinkFrameContextProvider>
            </GridContainer>
          </ReviewSaveButtonContextProvider>
        </>
      )}

      {isInteractionFinished && (
        <GridContainer>
          <Success />
        </GridContainer>
      )}
    </BlueprintsContext.Provider>
  )
}

export default InteractionContainer
